import { Fragment, useMemo } from 'react';
import { ThreadStatusResult } from '@weave/schema-gen-ts/dist/schemas/sms/data/v4/sms_service.pb';
import { useTranslation } from '@frontend/i18n';
import { InboxLocationPhones, InboxLocationPhonesTypes } from '@frontend/inbox-location-phones';
import { useAppScopeStore } from '@frontend/scope';
import { useAppFlagStore } from '@frontend/shared';
import { theme } from '@frontend/theme';
import { PopoverMenuItem, Text, usePopoverMenu } from '@frontend/design-system';
import { OldPhoneOption } from './old-phone-option';
import { PhoneOption } from './phone-option';
import { PopoverMenuTextItem } from './popover-menu-text-item';

type Props = {
  activeConversationCount: number;
  activeIndex: number;
  conversationsByLocation: Record<string, ThreadStatusResult[]>;
  getItemProps: ReturnType<typeof usePopoverMenu>['getItemProps'];
  locationIds: string[];
  locationPhones: Record<string, InboxLocationPhonesTypes.LocationPhone[]>;
  onAllOutboundNumbersClick: () => void;
  onSelectConversation: (locationPhone: string, locationId: string, threadId?: string) => void;
  selectedLocationPhone?: string;
};

export const ActiveConversations = ({
  activeConversationCount,
  activeIndex,
  conversationsByLocation,
  getItemProps,
  locationIds,
  locationPhones,
  onAllOutboundNumbersClick,
  onSelectConversation,
  selectedLocationPhone,
}: Props) => {
  const { t } = useTranslation('inbox');
  const { getScopeName } = useAppScopeStore();
  const conversationsByLocationArray = useMemo(
    () => Object.entries(conversationsByLocation),
    [conversationsByLocation]
  );
  const extendedConversationsByLocationArray = useMemo(() => {
    return conversationsByLocationArray.reduce<
      [string, (ThreadStatusResult & { defaultSms?: boolean; displayName: string; defaultAi?: boolean })[]][]
    >((acc, [locationId, conversations]) => {
      const sortedConversations = conversations
        .map((conversation) => {
          const foundLocationPhone = locationPhones[locationId]?.find(
            ({ locationPhone }) => locationPhone === conversation.locationPhone
          );
          const defaultSms = foundLocationPhone?.defaultSms;
          const displayName = foundLocationPhone?.displayName ?? '';
          const defaultAi = foundLocationPhone?.defaultAi ?? false;

          return {
            ...conversation,
            defaultSms,
            displayName,
            defaultAi,
          };
        })
        .sort((a, b) =>
          InboxLocationPhones.Utils.sortLocationPhones(
            { locationPhone: a.locationPhone, displayName: a.displayName, defaultSms: a.defaultSms },
            { locationPhone: b.locationPhone, displayName: b.displayName, defaultSms: b.defaultSms }
          )
        );
      acc.push([locationId, sortedConversations]);
      return acc;
    }, []);
  }, [conversationsByLocationArray, locationPhones]);

  // TODO: remove this logic when we deprecate the `phone-numbers-name-id` FF
  const { getFeatureFlagValue } = useAppFlagStore();
  const shouldUseV4 = getFeatureFlagValue({
    locationIds,
    flagName: 'phone-numbers-name-id',
    strategy: 'ALL',
  });
  // END OF TODO

  if (activeConversationCount === 0) {
    return null;
  }

  return (
    <>
      <PopoverMenuTextItem
        css={{ fontWeight: 'bold', height: 40, display: 'flex', alignItems: 'center', padding: theme.spacing(0, 2) }}
        data-trackingid='location-phone-selection-menu-header'
      >
        {t('Recent Activity')}
      </PopoverMenuTextItem>
      {extendedConversationsByLocationArray.map(([locationId, conversations], i) => (
        <Fragment key={`${locationId}-${t('Recent Activity').toLowerCase()}-options`}>
          {Object.keys(locationPhones).length > 1 && (
            <PopoverMenuTextItem
              iconName='location'
              data-trackingid='location-phone-selection-menu-location-header'
              css={{ fontWeight: 'bold' }}
            >
              {getScopeName(locationId)}
            </PopoverMenuTextItem>
          )}
          {conversations.map((conversation, conversationIndex) => {
            const index = i + conversationIndex;

            return (
              <PopoverMenuItem
                key={conversation.threadId}
                {...getItemProps({
                  index,
                  onClick: () => {
                    onSelectConversation(conversation.locationPhone, conversation.locationId, conversation.threadId);
                  },
                })}
                trackingId='inbox-thread-new-message-location-phone-option'
                data-value={conversation.locationPhone}
                data-threadid={conversation.threadId}
                css={[
                  conversation.displayName && {
                    paddingTop: theme.spacing(0.5),
                    paddingBottom: theme.spacing(0.5),
                    minHeight: 'fit-content',
                  },
                  conversation.locationPhone === selectedLocationPhone && {
                    borderLeft: `4px solid ${theme.colors.primary50}`,
                  },
                ]}
              >
                {/* TODO: remove this logic when we deprecate the `phone-numbers-name-id` FF */}
                {!shouldUseV4 && (
                  <OldPhoneOption
                    defaultSms={conversation.defaultSms}
                    displayName={conversation.displayName}
                    isActive={activeIndex === index}
                    phoneNumber={conversation.locationPhone}
                  />
                )}
                {/* END OF TODO */}
                {shouldUseV4 && (
                  <PhoneOption
                    defaultSms={conversation.defaultSms}
                    displayName={conversation.displayName}
                    isActive={activeIndex === index}
                    locationId={conversation.locationId}
                    phoneNumber={conversation.locationPhone}
                  />
                )}
              </PopoverMenuItem>
            );
          })}
        </Fragment>
      ))}
      <div css={{ width: '100%', height: 1, background: theme.colors.neutral20, margin: theme.spacing(0.5, 0) }} />
      <PopoverMenuItem
        {...getItemProps({
          index: activeConversationCount + 1,
          onClick: onAllOutboundNumbersClick,
          disableCloseOnSelect: true,
        })}
        css={{
          alignItems: 'center',
          padding: theme.spacing(1.5, 2),
          justifyContent: 'space-between',
        }}
        trackingId='location-phone-conversation-submenu'
      >
        <Text size='medium' weight='medium'>
          {t('All Outbound Numbers...')}
        </Text>
      </PopoverMenuItem>
    </>
  );
};
